import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { BackgroundPrefabs, BeautyFilterBanubaState, MakeupPrefabs } from 'types/beautyFilter';
import { MAKEUP_KEYS } from 'constants/beautyFilterTables';
import beautyFilterBridge from 'bridges/beautyFilterBridge';
import { baBanubaUnavailable } from 'utils/beautyFilterUtils';

const BANUBA_SCENE = 'mine';
const BANUBA_VERSION = '2.0.0';
const BANUBA_STORAGE_KEY = 'BANUBA_PREFAB';

const PRESET_BASE_FILTER = 50;

// GPU hang 기기에서는 SDK 초기화가 무기한 끝나지 않을 수 있다. 이 시간 안에 못 끝내면
// 뷰티·메이크업 UI를 사용 불가로 표시한다 (늦게라도 성공하면 자동 해제).
const BANUBA_INIT_TIMEOUT_MS = 10000;

const createDefaultState = (): BeautyFilterBanubaState => ({
  scene: BANUBA_SCENE,
  version: BANUBA_VERSION,
  faces: [{ morphing: {} }],
  background: null,
});

interface BeautyFilterBanubaStore {
  state: BeautyFilterBanubaState;
  getState: () => BeautyFilterBanubaState;
  setState: (state: BeautyFilterBanubaState) => void;

  presetValue: number; // 0-100
  presetType: 'all' | 'withoutEyes';
  setPresetValue: (presetValue: number) => void;
  setPresetType: (presetType: 'all' | 'withoutEyes') => void;

  isInitialized: boolean;
  // 초기화가 타임아웃되거나 실패한 상태. 뷰티·메이크업 탭을 막는 근거 (무드/스티커는 Banuba 무관이라 영향 없음)
  banubaUnavailable: boolean;
  // 네이티브 GPU passthrough(리드백 회피) 기기. 무드·스티커·뷰티·메이크업 전부 막고 Banuba 초기화도 하지 않는다.
  // 세션마다 네이티브가 알려주므로 persist 대상이 아니다.
  gpuPassthrough: boolean;
  setGpuPassthrough: (gpuPassthrough: boolean) => void;
  initializeBanuba: () => Promise<boolean>;
  isSetted: boolean;
  isSettedFor: '' | 'prefab' | 'preset' | 'both';
  setBanuba: (assetName: string) => Promise<boolean>;

  // applyBanuba 내부 직렬화용 (외부에서 사용하지 말 것)
  applyingBanuba: boolean;
  applyBanubaPending: boolean;
  applyBanubaOnce: () => Promise<void>;

  resetState: () => void;
  resetBeautyState: (key?: string) => void;
  resetMakeupState: (key?: string) => void;
  resetBackgroundState: () => void;
  resetPresetValue: () => void;

  updateBeautyState: (key: string, subKey: string, value: number) => void;
  updateMakeupState: (key: string, value: number, banubaPrefabs: MakeupPrefabs) => void;
  updateBackgroundState: (background: BackgroundPrefabs) => void;
  updatePresetValue: (presetValue: number) => void;

  getBeautyValue: (key: string, subKey: string) => number;
  getMakeupValue: (key: string, color: string) => number;
  getBackgroundValue: () => BackgroundPrefabs | null;
  getPresetValue: () => number;

  isUsingBeauty: () => boolean;
  isUsingMakeup: () => boolean;
  isUsingBanuba: () => boolean;
  isBeautyKeyUsing: (key: string) => boolean;
  isBeautySubKeyUsing: (key: string, subKey: string) => boolean;
  isMakeupKeyUsing: (key: string) => boolean;
  isBackgroundUsing: (background: BackgroundPrefabs) => boolean;
  isUsingPreset: () => boolean;

  applyBanuba: () => Promise<void>;
}

// 재시도 로직을 위한 헬퍼 함수
const withRetry = async (operation: () => Promise<boolean>, maxRetries = 3, delay = 500): Promise<boolean> => {
  for (let retryCount = 0; retryCount < maxRetries; retryCount++) {
    try {
      const result = await operation();
      if (result) {
        return result;
      }
    } catch (error) {
      if (retryCount === maxRetries - 1) {
        throw error;
      }
      await new Promise((resolve) => setTimeout(resolve, delay * (retryCount + 1)));
    }
  }
  throw new Error('Operation failed after all retries');
};

const useBeautyFilterBanubaStore = create<BeautyFilterBanubaStore>()(
  persist(
    (set, get) => ({
      state: createDefaultState(),
      getState: () => get().state,
      setState: (state: BeautyFilterBanubaState) => set({ state }),
      presetValue: 0,
      presetType: 'withoutEyes',
      setPresetValue: (presetValue: number) => set({ presetValue }),
      setPresetType: (presetType: 'all' | 'withoutEyes') => set({ presetType }),
      isSettedFor: '',
      // initialize
      isInitialized: false,
      banubaUnavailable: false,
      gpuPassthrough: false,
      setGpuPassthrough: (gpuPassthrough: boolean) => set({ gpuPassthrough }),
      isSetted: false,
      initializeBanuba: async () => {
        // passthrough 기기는 Banuba를 쓸 일이 없다. SDK 로드 자체를 생략 (banubaUnavailable 판정·ba 보고도 하지 않음)
        if (get().gpuPassthrough) return false;

        const _initializeBanubaSdk = async (): Promise<boolean> => {
          // 초기화 확인
          const isInitialized = await beautyFilterBridge.isInitializedBanubaSdkAsync();
          // 초기화가 안되어 있으면 초기화 시도
          if (!isInitialized) {
            const initializeResult = await beautyFilterBridge.initializeBanubaSdkAsync();
            if (!initializeResult) return false;
          }
          return true;
        };

        const _downloadAsset = async (assetName: string): Promise<boolean> => {
          // 애셋 다운로드 확인
          const isAssetDownloaded = await beautyFilterBridge.isBanubaAssetDownloadedAsync(assetName);
          // 애셋이 다운로드되지 않았으면 다운로드 시도
          if (!isAssetDownloaded) {
            const downloadResult = await beautyFilterBridge.downloadBanubaAssetResourceAsync(assetName);
            if (!downloadResult) return false;
          }
          return true;
        };

        // GPU hang 기기에서는 SDK 초기화 브리지 콜백이 무기한 안 돌아온다. 워치독으로 사용 불가 표시만 하고
        // await 자체는 계속 대기 — GPU가 회복되면 이어서 성공하고 아래에서 플래그를 되돌린다.
        // 초기화 실패(init_failed)와 애셋 다운로드 실패(asset_failed)는 원인이 달라 따로 보고한다.
        try {
          const watchdog = setTimeout(() => {
            if (!get().banubaUnavailable) {
              set({ banubaUnavailable: true });
              baBanubaUnavailable('timeout');
            }
          }, BANUBA_INIT_TIMEOUT_MS);
          try {
            await withRetry(async () => await _initializeBanubaSdk(), 3, 500); // 3회 재시도, 500ms 간격
          } finally {
            clearTimeout(watchdog);
          }
        } catch (error) {
          BLog.error('Banuba initialization failed:', error);
          if (!get().banubaUnavailable) {
            set({ banubaUnavailable: true });
            baBanubaUnavailable('init_failed');
          }
          return false;
        }

        try {
          await withRetry(async () => await _downloadAsset('Makeup'), 3, 500); // 3회 재시도, 500ms 간격

          // 발열 최적화
          Device.setBanubaFaceSearchMode('FAST');
          const unusedAssets = ['Hair', 'Hands', 'Eyes', 'Body', 'AcneEyebagsRemoval', 'PoseEstimation', 'FaceAttributes', 'FaceMatch', 'Occlusion', 'Watch', 'Neck'];
          for (const asset of unusedAssets) {
            Device.removeBanubaAssetResource(asset);
          }

          set({ isInitialized: true, banubaUnavailable: false });
          return true;
        } catch (error) {
          BLog.error('Banuba asset download failed:', error);
          if (!get().banubaUnavailable) {
            set({ banubaUnavailable: true });
            baBanubaUnavailable('asset_failed');
          }
          return false;
        }
      },
      setBanuba: async (assetName: string) => {
        const _setBanuba = async (assetName: string): Promise<boolean> => {
          const setBanubaResult = await beautyFilterBridge.setBanubaAsync(assetName);
          if (!setBanubaResult) return false;
          return true;
        };
        try {
          await withRetry(async () => await _setBanuba(assetName), 3, 500); // 3회 재시도, 500ms 간격
          set({ isSetted: true });
          return true;
        } catch (error) {
          BLog.error('Banuba asset setup failed:', error);
          return false;
        }
      },
      // resetState
      resetState: () => {
        set({ state: createDefaultState() });
      },
      resetBeautyState: (key?: string) => {
        const newState: BeautyFilterBanubaState = { ...get().state };
        if (key) {
          delete newState.faces[0].morphing[key];
        } else {
          newState.faces[0].morphing = {};
        }
        set({ state: newState });
      },
      resetMakeupState: (key?: string) => {
        const newState: BeautyFilterBanubaState = { ...get().state };
        if (key) {
          delete newState.faces[0][key];
        } else {
          MAKEUP_KEYS.forEach((makeupKey) => {
            delete newState.faces[0][makeupKey];
          });
        }
        set({ state: newState });
      },
      resetBackgroundState: () => {
        const newState: BeautyFilterBanubaState = { ...get().state };
        newState.background = null;
        set({ state: newState });
      },
      resetPresetValue: () => {
        set({ presetValue: 0 });
      },
      // updateState
      updateBeautyState: (key: string, subKey: string, value: number) => {
        const newState: BeautyFilterBanubaState = { ...get().state };
        if (value) {
          newState.faces[0].morphing[key] = {
            ...newState.faces[0].morphing[key],
            [subKey]: value,
          };
        } else {
          delete newState.faces[0].morphing[key]?.[subKey];
        }
        set({ state: newState });
      },
      updateMakeupState: (key: string, value: number, banubaPrefabs: MakeupPrefabs) => {
        const newState: BeautyFilterBanubaState = { ...get().state };
        if (value) {
          newState.faces[0][key] = { ...banubaPrefabs, coverage: value } as MakeupPrefabs;
        } else {
          delete newState.faces[0][key];
        }
        set({ state: newState });
      },
      updateBackgroundState: (background: BackgroundPrefabs) => {
        const newState: BeautyFilterBanubaState = { ...get().state };
        newState.background = background;
        set({ state: newState });
      },
      updatePresetValue: (presetValue: number) => set({ presetValue }),
      // getValue
      getBeautyValue: (key: string, subKey: string) => {
        const state = get().state;
        return state.faces[0].morphing[key]?.[subKey] || 0;
      },
      getMakeupValue: (key: string, color: string) => {
        const state = get().state;
        return state.faces[0][key]?.color === color ? state.faces[0][key]?.coverage || 0 : 0;
      },
      getBackgroundValue: () => {
        const state = get().state;
        return state.background;
      },
      getPresetValue: () => {
        const presetValue = get().presetValue;
        return presetValue;
      },
      // using
      isUsingBeauty: () => {
        const state = get().state;
        return Object.keys(state.faces[0].morphing).length > 0;
      },
      isUsingMakeup: () => {
        const state = get().state;
        return MAKEUP_KEYS.some((key) => state.faces[0][key]);
      },
      isUsingBanuba: () => {
        const usingBeauty = get().isUsingBeauty();
        const usingMakeup = get().isUsingMakeup();
        return usingBeauty || usingMakeup;
      },
      isBeautyKeyUsing: (key: string) => {
        const state = get().state;
        return state.faces[0].morphing[key] ? true : false;
      },
      isBeautySubKeyUsing: (key: string, subKey: string) => {
        const state = get().state;
        return state.faces[0].morphing[key]?.[subKey] ? true : false;
      },
      isMakeupKeyUsing: (key: string) => {
        const state = get().state;
        return state.faces[0][key] ? true : false;
      },
      isBackgroundUsing: (background: BackgroundPrefabs) => {
        const state = get().state;
        return state.background === background;
      },
      isUsingPreset: () => {
        const presetValue = get().presetValue;
        return presetValue > 0;
      },
      // applyState
      applyingBanuba: false,
      applyBanubaPending: false,
      applyBanuba: async () => {
        // 슬라이더 드래그 등으로 연속 호출될 때 겹쳐 실행되면 NONE/Makeup 순서가 꼬이므로
        // 한 번에 하나만 실행하고, 실행 중 들어온 요청은 마지막 상태 기준으로 1회만 재실행한다.
        if (get().applyingBanuba) {
          set({ applyBanubaPending: true });
          return;
        }

        set({ applyingBanuba: true });
        try {
          do {
            set({ applyBanubaPending: false });
            await get().applyBanubaOnce();
          } while (get().applyBanubaPending);
        } finally {
          set({ applyingBanuba: false });
        }
      },
      applyBanubaOnce: async () => {
        if (get().gpuPassthrough) return;

        if (!get().isInitialized) {
          await get().initializeBanuba();
          await new Promise((resolve) => setTimeout(resolve, 300));
        }

        const usingPrefab = get().isUsingBanuba();
        const usingPreset = get().isUsingPreset();
        const target = usingPrefab && usingPreset ? 'both' : usingPrefab ? 'prefab' : usingPreset ? 'preset' : '';

        // 둘 다 이용 안하면 + setBanuba 설정되어 있으면 setBanuba('NONE')
        if (target === '') {
          if (get().isSetted) {
            try {
              await get().setBanuba('NONE');
              set({ isSetted: false, isSettedFor: '' });
            } catch (error) {
              BLog.error('Beauty asset setup failed:', error);
            }
          }
          return;
        }

        try {
          // 이전 모드에 있던 효과가 새 모드에서 빠질 때만 NONE→Makeup 리로드로 잔여 효과를 제거한다.
          // (기존 코드는 prefab+preset 동시 사용 시 isSettedFor가 둘 사이를 왕복하며
          //  호출마다 리로드를 2회 반복해 이펙트 적용이 수 초씩 걸리고 Banuba에 부하를 줬음)
          const prev = get().isSettedFor;
          const removesPreset = (prev === 'preset' || prev === 'both') && !usingPreset;
          const removesPrefab = (prev === 'prefab' || prev === 'both') && !usingPrefab;
          const needReload = get().isSetted && (removesPreset || removesPrefab);

          if (needReload) {
            await get().setBanuba('NONE');
            await get().setBanuba('Makeup');
            set({ isSetted: true, isSettedFor: target });
            await new Promise((resolve) => setTimeout(resolve, 1000));
          } else if (!get().isSetted) {
            await get().setBanuba('Makeup');
            set({ isSetted: true, isSettedFor: target });
            await new Promise((resolve) => setTimeout(resolve, 1000));
          } else {
            set({ isSettedFor: target });
          }
        } catch (error) {
          BLog.error('Beauty asset setup failed:', error);
        }

        // prefab 이용 시
        if (usingPrefab) {
          Device.setBanubaPrefab(JSON.stringify(get().state));
        }
        // preset 이용 시
        if (usingPreset) {
          const presetValue = get().presetValue;
          const normalizedValue = (presetValue + PRESET_BASE_FILTER) / (100.0 + PRESET_BASE_FILTER);
          if (get().presetType === 'all') {
            // 전체 적용
            if (Device.name === 'android') {
              Device.callBanubaJsMethod('setBeauty', normalizedValue);
            } else if (Device.name === 'iOS') {
              Device.callBanubaJsMethod('Teeth.whitening', presetValue > 0 ? 1 : 0);
              Device.callBanubaJsMethod('FaceMorph.face', normalizedValue);
              Device.callBanubaJsMethod('FaceMorph.eyes', normalizedValue);
              Device.callBanubaJsMethod('FaceMorph.nose', normalizedValue);
              Device.callBanubaJsMethod('Skin.softening', presetValue > 0 ? 1 : 0);
              Device.callBanubaJsMethod('Softlight.strength', presetValue > 0 ? 1 : 0);
            }
          } else if (get().presetType === 'withoutEyes') {
            // 눈 제외
            if (Device.name === 'android') {
              Device.callBanubaJsMethod('setBeautyWithoutEyes', normalizedValue);
            } else if (Device.name === 'iOS') {
              Device.callBanubaJsMethod('Teeth.whitening', presetValue > 0 ? 1 : 0);
              Device.callBanubaJsMethod('FaceMorph.face', normalizedValue);
              Device.callBanubaJsMethod('FaceMorph.nose', normalizedValue);
              Device.callBanubaJsMethod('Skin.softening', presetValue > 0 ? 1 : 0);
              Device.callBanubaJsMethod('Softlight.strength', presetValue > 0 ? 1 : 0);
            }
          }
        }
      },
    }),
    {
      name: BANUBA_STORAGE_KEY,
      partialize: (state) => ({
        state: state.state,
        presetValue: state.presetValue,
      }),
    }
  )
);

export default useBeautyFilterBanubaStore;
